import React from 'react';
import {
    Button,
    ColumnLayout,
    Grid,
    Multiselect,
    Select,
    SpaceBetween,
    SelectProps,
    DateRangePicker,
    DateRangePickerProps,
    FormField,
} from '@amzn/awsui-components-react';
import {NodeDetail, NodeDetails} from '@amzn/f3-excelsior-analytics-api/clients/analyticsapiforexcelsioranalytics';

export default function BoscoExplainerFilters({
    filterValues,
    selectedFilter,
    setSelectedFilter,
    isLoadingFilterData,
    isLoadingData,
    onApply,
}: {
    filterValues: {
        nodes: NodeDetails;
        granularities: any;
        balanceDateEarliest: string;
        balanceDateLatest: string;
    };
    selectedFilter: {
        nodes: NodeDetails;
        granularity: any;
        balanceDateStart: string;
        balanceDateEnd: string;
    };
    setSelectedFilter: (filter: {nodes: NodeDetails; granularity: any; balanceDateStart: string; balanceDateEnd: string}) => void;
    isLoadingFilterData: boolean;
    isLoadingData: boolean;
    onApply: () => void;
}) {
    return (
        <SpaceBetween direction="vertical" size="m">
            <Grid
                gridDefinition={[
                    {colspan: 3},
                    ...Object.keys(filterValues.granularities).map((_) => ({
                        colspan: 3,
                    })),
                ]}
            >
                {renderDateRangePicker(filterValues.balanceDateEarliest, filterValues.balanceDateLatest)}
                {Object.keys(filterValues.granularities).map((granularity: string) =>
                    renderGranularityFilterMultiselect(granularity, [...filterValues.granularities[granularity]])
                )}
            </Grid>
            {renderNodeSelect(filterValues.nodes)}
            <Button disabled={isLoadingFilterData || isLoadingData} data-testid="page-filter-button" onClick={onApply}>
                Apply
            </Button>
        </SpaceBetween>
    );

    function renderNodeSelect(nodes: NodeDetails): React.ReactElement {
        return (
            <FormField label="Stores">
                <SpaceBetween size="m">
                    <ColumnLayout columns={2}>
                        <div>
                            <Multiselect
                                data-testid="page-filter-node-select"
                                placeholder="Select nodes/stores"
                                options={nodes.map((node) => ({label: node.nodeName, value: node.nodeId}))}
                                selectedOptions={selectedFilter.nodes.map((node: NodeDetail) => ({
                                    label: node.nodeName,
                                    value: node.nodeId,
                                }))}
                                onChange={async ({detail}) => {
                                    setSelectedFilter({
                                        ...selectedFilter,
                                        nodes: detail.selectedOptions.map((option: SelectProps.Option) => ({
                                            nodeName: option.label!,
                                            nodeId: option.value!,
                                        })),
                                    });
                                }}
                                tokenLimit={10}
                                filteringType="auto"
                            />
                        </div>
                        <div>
                            <ColumnLayout columns={2}>
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button
                                        onClick={async () => {
                                            setSelectedFilter({
                                                ...selectedFilter,
                                                nodes: filterValues.nodes.map((node: NodeDetail) => ({
                                                    nodeName: node.nodeName,
                                                    nodeId: node.nodeId,
                                                })),
                                            });
                                        }}
                                        data-testid="select-all-nodes-button"
                                    >
                                        Select All
                                    </Button>
                                    <Button
                                        onClick={async () => {
                                            setSelectedFilter({
                                                ...selectedFilter,
                                                nodes: [],
                                            });
                                        }}
                                        data-testid="deselect-all-nodes-button"
                                    >
                                        De-select All
                                    </Button>
                                </SpaceBetween>
                            </ColumnLayout>
                        </div>
                    </ColumnLayout>
                </SpaceBetween>
            </FormField>
        );
    }

    function renderDateRangePicker(balanceDateEarliest: string, balanceDateLatest: string): React.ReactElement {
        return (
            <FormField label="Balance Dates">
                <DateRangePicker
                    data-testid="page-filter-date-range-picker"
                    value={{
                        type: 'absolute',
                        startDate: selectedFilter?.balanceDateStart ?? '',
                        endDate: selectedFilter?.balanceDateEnd ?? '',
                    }}
                    warning={
                        selectedFilter
                            ? selectedFilter.balanceDateStart < balanceDateEarliest ||
                              selectedFilter.balanceDateEnd > balanceDateLatest
                            : false
                    }
                    isValidRange={(range) => {
                        if (range?.type === 'absolute' && selectedFilter) {
                            const [startDate] = range.startDate.split('T');
                            const [endDate] = range.endDate.split('T');
                            if (!startDate || !endDate) {
                                return {
                                    valid: false,
                                    errorMessage: 'The selected date range is incomplete.',
                                };
                            }
                            if (startDate < balanceDateEarliest) {
                                return {
                                    valid: false,
                                    errorMessage: `The selected date range must be after ${balanceDateEarliest}.`,
                                };
                            }
                            if (endDate > balanceDateLatest) {
                                return {
                                    valid: false,
                                    errorMessage: `The selected date range must be before ${balanceDateLatest}.`,
                                };
                            }
                        }
                        return {valid: true};
                    }}
                    onChange={({detail}) => {
                        if (selectedFilter) {
                            setSelectedFilter({
                                ...selectedFilter,
                                balanceDateStart: (detail.value as DateRangePickerProps.AbsoluteValue).startDate,
                                balanceDateEnd: (detail.value as DateRangePickerProps.AbsoluteValue).endDate,
                            });
                        }
                    }}
                    dateOnly={true}
                    relativeOptions={[]}
                    placeholder="Filter by a date range"
                    rangeSelectorMode="absolute-only"
                    i18nStrings={{
                        applyButtonLabel: 'Apply',
                        cancelButtonLabel: 'Cancel',
                        clearButtonLabel: 'Clear and dismiss',
                    }}
                />
            </FormField>
        );
    }

    function renderGranularityFilterMultiselect(granularity: string, values: string[]): React.ReactElement {
        return (
            <FormField key={granularity} label={granularity}>
                <Select
                    data-testid={`page-filter-${granularity}-multiselect`}
                    selectedOption={{
                        label: selectedFilter.granularity[granularity],
                        value: selectedFilter.granularity[granularity],
                    }}
                    onChange={({detail}) => {
                        if (selectedFilter && detail.selectedOption.value) {
                            setSelectedFilter({
                                ...selectedFilter,
                                granularity: {...selectedFilter.granularity, [granularity]: detail.selectedOption.value},
                            });
                        }
                    }}
                    options={values.map((gran: string) => ({
                        label: gran,
                        value: gran,
                    }))}
                    filteringType="auto"
                    placeholder={granularity}
                />
            </FormField>
        );
    }
}
